/**
 * Frontend Configuration Constants
 *
 * Single source of truth for all configurable values.
 * No more hardcoded values scattered across components!
 *
 * Categories:
 * - SCORING: Intent, fit, priority thresholds and weights
 * - DEFAULTS: Pagination, timeframes, limits
 * - QUEUE: Smart Queue action types and urgency levels
 * - UI: Animation durations, breakpoints
 *
 * Created: 2026-01-05
 */

// ====== SCORING THRESHOLDS ======

export const SCORING = {
  // Intent score thresholds (from IntentScoringService)
  INTENT_HOT: 61,      // Score >= 61 = Hot prospect
  INTENT_WARM: 21,     // Score 21-60 = Warm prospect
  INTENT_COLD: 0,      // Score 0-20 = Cold prospect

  // Fit score thresholds (from AnalyzerAgent)
  FIT_EXCELLENT: 80,   // Perfect ICP match
  FIT_GOOD: 60,        // Good fit
  FIT_AVERAGE: 40,     // Average fit
  FIT_POOR: 20,        // Poor fit

  // Priority scoring weights (must sum to 1.0)
  PRIORITY_INTENT_WEIGHT: 0.30,   // 30% - Ad engagement signals
  PRIORITY_FIT_WEIGHT: 0.25,      // 25% - ICP fit score
  PRIORITY_STAGE_WEIGHT: 0.25,    // 25% - Buying stage
  PRIORITY_URGENCY_WEIGHT: 0.20,  // 20% - Time sensitivity

  // Stage scores (0-100)
  STAGE_SCORES: {
    awareness: 10,
    interest: 30,
    consideration: 50,
    intent: 70,
    evaluation: 85,
    decision: 95,
  } as const,

  // Urgency thresholds
  URGENCY_CRITICAL: 90,
  URGENCY_HIGH: 70,
  URGENCY_MEDIUM: 40,
  URGENCY_LOW: 0,
} as const;


// ====== DEFAULT VALUES ======

export const DEFAULTS = {
  // Lookback periods (days)
  LOOKBACK_SHORT: 30,
  LOOKBACK_MEDIUM: 90,
  LOOKBACK_LONG: 180,

  // Pagination
  PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  QUEUE_LIMIT: 50,

  // Timeframes
  FOLLOW_UP_DUE_DAYS: 3,      // Days after sent to trigger follow-up
  STALE_PROSPECT_DAYS: 30,    // Days inactive to mark as stale
  SNOOZE_DEFAULT_HOURS: 24,   // Default snooze duration

  // Rate limits (for UI feedback)
  MAX_CONTACTS_PER_PROSPECT: 3,
  MIN_CONTACT_GAP_DAYS: 7,
} as const;


// ====== SMART QUEUE TYPES ======

export const QUEUE = {
  // Action types
  ACTION_TYPES: {
    HOT_LEAD: 'hot_lead',
    QUALIFIED_LEAD: 'qualified_lead',
    FOLLOW_UP_DUE: 'follow_up_due',
    MEETING_REQUEST: 'meeting_request',
    STALE_PROSPECT: 'stale_prospect',
  } as const,

  // Urgency levels
  URGENCY_LEVELS: {
    CRITICAL: 'critical',
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
  } as const,

  // Buying stages
  BUYING_STAGES: {
    AWARENESS: 'awareness',
    INTEREST: 'interest',
    CONSIDERATION: 'consideration',
    INTENT: 'intent',
    EVALUATION: 'evaluation',
    DECISION: 'decision',
  } as const,

  // Action type labels (for UI)
  ACTION_TYPE_LABELS: {
    hot_lead: 'Hot Lead',
    qualified_lead: 'Qualified Lead',
    follow_up_due: 'Follow-up Due',
    meeting_request: 'Meeting Request',
    stale_prospect: 'Stale Prospect',
  } as const,

  // Urgency colors (for badges)
  URGENCY_COLORS: {
    critical: 'bg-red-500 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-black',
    low: 'bg-gray-400 text-white',
  } as const,

  // Stage colors (for badges)
  STAGE_COLORS: {
    awareness: 'bg-blue-100 text-blue-800',
    interest: 'bg-cyan-100 text-cyan-800',
    consideration: 'bg-purple-100 text-purple-800',
    intent: 'bg-orange-100 text-orange-800',
    evaluation: 'bg-amber-100 text-amber-800',
    decision: 'bg-green-100 text-green-800',
  } as const,
} as const;


// ====== UI CONSTANTS ======

export const UI = {
  // Swipe gesture thresholds (pixels)
  SWIPE_THRESHOLD: 100,
  SWIPE_VELOCITY_THRESHOLD: 0.5,

  // Animation durations (ms)
  TRANSITION_FAST: 150,
  TRANSITION_NORMAL: 300,
  TRANSITION_SLOW: 500,

  // Toast durations (ms)
  TOAST_SUCCESS: 3000,
  TOAST_ERROR: 5000,
  TOAST_INFO: 4000,

  // Debounce delays (ms)
  DEBOUNCE_SEARCH: 300,
  DEBOUNCE_FILTER: 200,

  // Breakpoints (pixels)
  BREAKPOINT_SM: 640,
  BREAKPOINT_MD: 768,
  BREAKPOINT_LG: 1024,
  BREAKPOINT_XL: 1280,

  // Scroll reveal animation (for landing page sections)
  SCROLL_REVEAL_DISTANCE_PX: 40,
  SCROLL_REVEAL_THRESHOLD: 0.2,
  STAGGER_DELAY_MS: 100,
  // Phase 2: Focus trap delay
  FOCUS_TRAP_INITIAL_DELAY_MS: 100,
} as const;


// ====== WEBSOCKET CONSTANTS ======

export const WEBSOCKET = {
  CHECK_INTERVAL_MS: 100,
  RECONNECT_DELAY_MS: 1000,
  MAX_RECONNECT_ATTEMPTS: 5,
  // Phase 2: Max delay for exponential backoff
  RECONNECT_MAX_DELAY_MS: 10000,
} as const;


// ====== RETRY CONSTANTS ======

export const RETRY = {
  MAX_ATTEMPTS: 3,
  DELAYS_MS: [1000, 2000, 4000] as const,
  // Phase 2: Initial delay for retry backoff
  INITIAL_DELAY_MS: 1000,
} as const;


// ====== PAGINATION CONSTANTS ======

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  SETTINGS_PAGE_SIZE: 50,
  INVOICES_PAGE_SIZE: 12,
  SEQUENCE_PAGE_SIZE: 10,
  // Phase 2: Service-specific page sizes
  PROSPECT_PAGE_SIZE: 50,
  TEMPLATE_PAGE_SIZE: 50,
  TEMPLATE_RECOMMENDATIONS_LIMIT: 5,
  PLAYBOOK_LIMIT: 10,
  INTELLIGENCE_PAGE_SIZE: 20,
  METRIC_DETAIL_PAGE_SIZE: 50,
  COMPLIANCE_REPORTS_LIMIT: 10,
  AD_CAMPAIGNS_LIST_LIMIT: 50,
} as const;


// ====== WIZARD CONSTANTS ======

export const WIZARD = {
  CAMPAIGN_TOTAL_STEPS: 3,
} as const;


// ====== API ENDPOINTS ======

export const API = {
  QUEUE: '/queue',
  INTENT: '/intent',
  PROSPECTS: '/prospects',
  CAMPAIGNS: '/campaigns',
  ANALYTICS: '/analytics',
} as const;


// ====== EXTENSION ======

export const EXTENSION = {
  /** Chrome Web Store listing URL — empty string until published */
  CHROME_WEB_STORE_URL: import.meta.env.VITE_EXTENSION_STORE_URL || '',
} as const;


// ====== AGENT (M4) CONSTANTS ======

export const AGENT = {
  // Streaming
  STREAM_RETRY_DELAY_MS: 2000,
  MAX_STREAM_DURATION_MS: 120000,

  // Async query polling
  ASYNC_POLL_INTERVAL_MS: 3000,
  ASYNC_MAX_POLL_DURATION_MS: 300000,

  // WebSocket channels
  WS_PROGRESS_CHANNEL_PREFIX: 'agent_task:',
} as const;


// ====== API POLLING INTERVALS ======

export const API_POLLING = {
  // Phase 2: Document status polling
  DOCUMENT_POLLING_INTERVAL_MS: 2000,
} as const;


// ====== HELPER FUNCTIONS ======

/**
 * Get score status with color tokens and gradient classes.
 * Centralizes the 4-tier scoring pattern used across PortfolioHealthCard,
 * ComplianceSavingsCard, and other components.
 *
 * @param score - Score value (0-100)
 * @param thresholds - Custom thresholds (defaults to SCORING.FIT_* constants)
 */
export function getScoreStatus(
  score: number,
  thresholds?: { excellent?: number; good?: number; fair?: number }
): { status: string; colorToken: string; gradientClasses: string } {
  const excellent = thresholds?.excellent ?? SCORING.FIT_EXCELLENT;
  const good = thresholds?.good ?? SCORING.FIT_GOOD;
  const fair = thresholds?.fair ?? SCORING.FIT_AVERAGE;

  if (score >= excellent) return { status: 'Excellent', colorToken: '--state-success', gradientClasses: 'from-green-500 to-emerald-500' };
  if (score >= good) return { status: 'Good', colorToken: '--accent', gradientClasses: 'from-[var(--accent)] to-[var(--accent-hover)]' };
  if (score >= fair) return { status: 'Fair', colorToken: '--state-warning', gradientClasses: 'from-yellow-500 to-orange-500' };
  return { status: 'Needs Attention', colorToken: '--state-danger', gradientClasses: 'from-red-500 to-orange-500' };
}

/**
 * Get warmth label and color for an intent score
 */
export function getWarmthInfo(score: number): { label: string; color: string; emoji: string } {
  if (score >= SCORING.INTENT_HOT) {
    return { label: 'Hot', color: 'text-red-500', emoji: '🔥' };
  } else if (score >= SCORING.INTENT_WARM) {
    return { label: 'Warm', color: 'text-yellow-500', emoji: '☀️' };
  } else {
    return { label: 'Cold', color: 'text-blue-500', emoji: '❄️' };
  }
}

/**
 * Get fit label and color for a fit score
 */
export function getFitInfo(score: number): { label: string; color: string } {
  if (score >= SCORING.FIT_EXCELLENT) {
    return { label: 'Excellent', color: 'text-green-500' };
  } else if (score >= SCORING.FIT_GOOD) {
    return { label: 'Good', color: 'text-blue-500' };
  } else if (score >= SCORING.FIT_AVERAGE) {
    return { label: 'Average', color: 'text-yellow-500' };
  } else {
    return { label: 'Poor', color: 'text-red-500' };
  }
}

/**
 * Get urgency label and styles
 */
export function getUrgencyInfo(level: string): { label: string; className: string } {
  const colors = QUEUE.URGENCY_COLORS[level as keyof typeof QUEUE.URGENCY_COLORS] || QUEUE.URGENCY_COLORS.low;
  const labels: Record<string, string> = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };
  return { label: labels[level] || 'Unknown', className: colors };
}

/**
 * Get stage label and styles
 */
export function getStageInfo(stage: string): { label: string; className: string } {
  const colors = QUEUE.STAGE_COLORS[stage as keyof typeof QUEUE.STAGE_COLORS] || 'bg-gray-100 text-gray-800';
  const labels: Record<string, string> = {
    awareness: 'Awareness',
    interest: 'Interest',
    consideration: 'Consideration',
    intent: 'Intent',
    evaluation: 'Evaluation',
    decision: 'Decision',
  };
  return { label: labels[stage] || 'Unknown', className: colors };
}

/**
 * Calculate priority from component scores
 */
export function calculatePriority(
  intent: number,
  fit: number,
  stage: number,
  urgency: number
): number {
  const priority =
    intent * SCORING.PRIORITY_INTENT_WEIGHT +
    fit * SCORING.PRIORITY_FIT_WEIGHT +
    stage * SCORING.PRIORITY_STAGE_WEIGHT +
    urgency * SCORING.PRIORITY_URGENCY_WEIGHT;

  return Math.max(0, Math.min(100, Math.round(priority)));
}



// ====== CAMPAIGN PRESETS (TAM-ALIGNED) ======

/**
 * Campaign Creation Presets
 *
 * TAM-aligned presets that abstract technical decisions from users.
 * Instead of selecting delay/rate/LLM settings, users pick a use case.
 *
 * Benefits:
 * - Reduces decision paralysis (Perceived Ease of Use)
 * - Ensures optimal settings per use case (Perceived Usefulness)
 * - Power users can still access Advanced Settings
 */
export const CAMPAIGN_PRESETS = {
  stealth: {
    id: 'stealth',
    label: 'Careful Approach',
    description: 'Low volume, maximum personalization for high-value targets',
    icon: 'shield',
    recommended: false,
    defaults: {
      delay: 60,
      maxPerHour: 10,
      aiEnabled: true,
      temperature: 0.5,
      maxRetries: 5,
    },
    estimates: {
      websitesPerHour: 10,
      dailyCapacity: 240,
      quality: 'Highest',
      risk: 'Lowest',
    },
  },
  standard: {
    id: 'standard',
    label: 'Balanced Reach',
    description: 'Moderate pace with good personalization. Recommended for most campaigns.',
    icon: 'target',
    recommended: true,
    defaults: {
      delay: 30,
      maxPerHour: 20,
      aiEnabled: true,
      temperature: 0.7,
      maxRetries: 3,
    },
    estimates: {
      websitesPerHour: 20,
      dailyCapacity: 480,
      quality: 'High',
      risk: 'Low',
    },
  },
  aggressive: {
    id: 'aggressive',
    label: 'Fast Outreach',
    description: 'High volume for time-sensitive campaigns',
    icon: 'zap',
    recommended: false,
    defaults: {
      delay: 15,
      maxPerHour: 50,
      aiEnabled: true,
      temperature: 0.8,
      maxRetries: 2,
    },
    estimates: {
      websitesPerHour: 50,
      dailyCapacity: 1200,
      quality: 'Good',
      risk: 'Medium',
    },
  },
} as const;


// ====== LINKEDIN CAMPAIGN PRESETS ======

/**
 * LinkedIn-specific Campaign Presets
 *
 * Designed around LinkedIn's rate limits and best practices:
 * - Connection requests: ~100/day (LinkedIn limit)
 * - Messages: ~50-100/day to avoid restrictions
 * - Cooldown: 72 hours between touches per prospect
 *
 * These presets help users stay within safe limits while
 * maximizing outreach effectiveness.
 */
export const LINKEDIN_PRESETS = {
  linkedin_conservative: {
    id: 'linkedin_conservative',
    label: 'Safe Networking',
    description: 'Low volume to avoid LinkedIn restrictions. Best for new accounts.',
    icon: 'shield',
    recommended: false,
    defaults: {
      connectionsPerHour: 5,
      messagesPerHour: 3,
      dailyConnectionLimit: 50,
      dailyMessageLimit: 30,
      aiEnabled: true,
      temperature: 0.5,
      cooldownHours: 72,
    },
    estimates: {
      connectionsPerDay: 50,
      messagesPerDay: 30,
      quality: 'Highest',
      risk: 'Lowest',
    },
  },
  linkedin_standard: {
    id: 'linkedin_standard',
    label: 'Balanced Outreach',
    description: 'Moderate pace with good personalization. Recommended for most LinkedIn campaigns.',
    icon: 'target',
    recommended: true,
    defaults: {
      connectionsPerHour: 10,
      messagesPerHour: 6,
      dailyConnectionLimit: 80,
      dailyMessageLimit: 50,
      aiEnabled: true,
      temperature: 0.7,
      cooldownHours: 72,
    },
    estimates: {
      connectionsPerDay: 80,
      messagesPerDay: 50,
      quality: 'High',
      risk: 'Low',
    },
  },
  linkedin_aggressive: {
    id: 'linkedin_aggressive',
    label: 'Maximum Reach',
    description: 'High volume for established accounts. Risk of temporary restrictions.',
    icon: 'zap',
    recommended: false,
    defaults: {
      connectionsPerHour: 15,
      messagesPerHour: 10,
      dailyConnectionLimit: 100,
      dailyMessageLimit: 80,
      aiEnabled: true,
      temperature: 0.8,
      cooldownHours: 48,
    },
    estimates: {
      connectionsPerDay: 100,
      messagesPerDay: 80,
      quality: 'Good',
      risk: 'Medium',
    },
  },
} as const;

// LinkedIn Preset types for TypeScript
export type LinkedInPresetId = keyof typeof LINKEDIN_PRESETS;
export type LinkedInPreset = typeof LINKEDIN_PRESETS[LinkedInPresetId];

/**
 * Get LinkedIn preset configuration by ID
 */
export function getLinkedInPreset(presetId: LinkedInPresetId): LinkedInPreset {
  return LINKEDIN_PRESETS[presetId];
}

/**
 * Get all LinkedIn presets as array (for UI rendering)
 */
export function getLinkedInPresetsArray(): LinkedInPreset[] {
  return Object.values(LINKEDIN_PRESETS);
}

// Campaign Preset types for TypeScript
export type CampaignPresetId = keyof typeof CAMPAIGN_PRESETS;
export type CampaignPreset = typeof CAMPAIGN_PRESETS[CampaignPresetId];

/**
 * Get campaign preset configuration by ID
 */
export function getCampaignPreset(presetId: CampaignPresetId): CampaignPreset {
  return CAMPAIGN_PRESETS[presetId];
}

/**
 * Get all campaign presets as array (for UI rendering)
 */
export function getCampaignPresetsArray(): CampaignPreset[] {
  return Object.values(CAMPAIGN_PRESETS);
}

/**
 * Find matching preset for given settings (for playbook application)
 */
export function findMatchingCampaignPreset(
  delay: number,
  maxPerHour: number
): CampaignPresetId | null {
  for (const [presetId, preset] of Object.entries(CAMPAIGN_PRESETS)) {
    const presetDelay = preset.defaults.delay;
    const presetMax = preset.defaults.maxPerHour;

    // Check for close match (within 20% tolerance)
    const delayMatch = presetDelay > 0
      ? Math.abs(delay - presetDelay) <= presetDelay * 0.2
      : delay === 0;
    const maxMatch = presetMax > 0
      ? Math.abs(maxPerHour - presetMax) <= presetMax * 0.2
      : maxPerHour === 0;

    if (delayMatch && maxMatch) {
      return presetId as CampaignPresetId;
    }
  }
  return null;
}


// ====== TYPE EXPORTS ======

export type ActionType = typeof QUEUE.ACTION_TYPES[keyof typeof QUEUE.ACTION_TYPES];
export type UrgencyLevel = typeof QUEUE.URGENCY_LEVELS[keyof typeof QUEUE.URGENCY_LEVELS];
export type BuyingStage = typeof QUEUE.BUYING_STAGES[keyof typeof QUEUE.BUYING_STAGES];
